import {
  DEFAULT_AGE_RANGE,
  DEFAULT_GPR_RANGE,
  DEFAULT_CONTRACT_VALUE_RANGE,
  DEFAULT_METRIC_SCORE_RANGE,
  METRIC_RANGE_KEYS,
  type MetricRangeKey,
} from "../constants/filterDefaults";

/**
 * Represents the current state of all available filters
 */
interface FilterState {
  positions: string[];
  ageRange: [number, number];
  gprRange: [number, number];
  contractValueRange: [number, number];
  teams: string[];
  contractStatus?: string[];
  tags?: string[];
  /* SE-6871: the PERFORMANCE / GOALKEEPER metric ranges, keyed as in
     `METRIC_RANGE_KEYS`. Optional so pre-SE-6871 persisted state parses. */
  carryingRange?: [number, number];
  dribblingRange?: [number, number];
  passingRange?: [number, number];
  shootingRange?: [number, number];
  gkPassingRange?: [number, number];
  gkShotStoppingRange?: [number, number];
  gkPositioningRange?: [number, number];
}

/**
 * How many of the metric ranges have been narrowed from the fixed 0–100 span
 * (SE-6871). One place, so the total and the section badges cannot drift.
 * @param activeFilters - The filter state being counted.
 * @param keys - Which metric keys to count.
 * @returns Active count among those keys.
 */
const countNarrowedMetricRanges = (
  activeFilters: FilterState,
  keys: readonly MetricRangeKey[]
): number =>
  keys.filter(key => {
    const range = activeFilters[key];
    return range != null && isRangeNarrowed(range, DEFAULT_METRIC_SCORE_RANGE);
  }).length;

/**
 * Whether a selected range NARROWS the span available to it.
 *
 * A range that still spans its full bounds is not a filter, which is why the
 * comparisons are strict.
 * @param value - The selected [min, max]
 * @param bounds - The full [min, max] span available
 * @returns True when either end has been pulled in
 */
const isRangeNarrowed = (
  value: [number, number],
  bounds: [number, number]
): boolean => value[0] > bounds[0] || value[1] < bounds[1];

/**
 * Calculate the number of active filters that differ from their default values.
 *
 * SE-6554 made this the SINGLE source of the filter count. The drawer used to
 * compute its own with hardcoded baselines ([18,40] age, [0,10] GPR, [0,500]
 * value) that matched none of the real defaults, so a freshly opened board
 * reported three active filters when nothing was set, and the drawer and the
 * toolbar badge disagreed. Both now call this.
 *
 * A range counts as active only when it NARROWS the available span, which is
 * why the comparisons are strict against the dynamic bounds.
 * @param activeFilters - Current filter state containing all filter values
 * @param dynamicGprRange - Dynamic GPR range bounds based on available data (optional, falls back to defaults)
 * @param dynamicContractValueRange - Dynamic contract value range bounds based on available data (optional, falls back to defaults)
 * @returns Count of filters with non-default values
 */
export const calculateActiveFilterCount = (
  activeFilters: FilterState,
  dynamicGprRange?: [number, number],
  dynamicContractValueRange?: [number, number]
): number => {
  // eslint-disable-next-line functional/no-let -- Count accumulation requires mutation
  let count = 0;

  // Use dynamic ranges if provided, otherwise fall back to defaults
  const gprRangeToCheck = dynamicGprRange || DEFAULT_GPR_RANGE;
  const contractValueRangeToCheck =
    dynamicContractValueRange || DEFAULT_CONTRACT_VALUE_RANGE;

  if (activeFilters.positions.length > 0) count++;
  if (isRangeNarrowed(activeFilters.ageRange, DEFAULT_AGE_RANGE)) count++;
  if (isRangeNarrowed(activeFilters.gprRange, gprRangeToCheck)) count++;
  if (
    isRangeNarrowed(activeFilters.contractValueRange, contractValueRangeToCheck)
  )
    count++;
  if (activeFilters.teams.length > 0) count++;
  if (activeFilters.contractStatus && activeFilters.contractStatus.length > 0)
    count++;
  if (activeFilters.tags && activeFilters.tags.length > 0) count++;
  count += countNarrowedMetricRanges(activeFilters, METRIC_RANGE_KEYS);
  return count;
};
